import type { SupabaseClient } from "@supabase/supabase-js";

import type {
  CyanMemorySourceReference,
  MemoryRetrievalScope,
  PersistentCyanMemoryRecord,
  PersistentMemoryRepository,
  SavePersistentMemoryInput,
} from "./persistentMemoryRepository";

type DbMemoryRow = {
  id: string;
  organization_id: string;
  user_id: string | null;
  project_id: string | null;
  conversation_id: string | null;
  message_id: string | null;
  memory_type: string;
  content: string;
  source_type: string;
  source_id: string;
  source_path: string | null;
  source_version: string | null;
  confidence: number;
  embedding_status:
    "pending" | "processing" | "ready" | "failed" | "not_required";
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

function mapRow(row: DbMemoryRow): PersistentCyanMemoryRecord {
  return {
    id: row.id,
    organizationId: row.organization_id,
    userId: row.user_id ?? undefined,
    projectId: row.project_id ?? undefined,
    conversationId: row.conversation_id ?? undefined,
    messageId: row.message_id ?? undefined,
    memoryType: row.memory_type,
    content: row.content,

    source: {
      type: row.source_type,
      id: row.source_id,
      path: row.source_path ?? undefined,
      version: row.source_version ?? undefined,
    },

    confidence: row.confidence,
    embeddingStatus: row.embedding_status,
    metadata: row.metadata ?? {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function escapeLike(value: string): string {
  return value
    .replaceAll("\\", "\\\\")
    .replaceAll("%", "\\%")
    .replaceAll("_", "\\_");
}

export class SupabasePersistentMemoryRepository implements PersistentMemoryRepository {
  constructor(private readonly supabase: SupabaseClient) {}

  async save(
    input: SavePersistentMemoryInput,
  ): Promise<PersistentCyanMemoryRecord> {
    if (!input.source.type.trim()) {
      throw new Error("Memory source type is required.");
    }

    if (!input.source.id.trim()) {
      throw new Error("Memory source id is required.");
    }

    const { data, error } = await this.supabase
      .from("cyan_memory_records")
      .insert({
        organization_id: input.organizationId,

        user_id: input.userId ?? null,

        project_id: input.projectId ?? null,

        conversation_id: input.conversationId ?? null,

        message_id: input.messageId ?? null,

        memory_type: input.memoryType,

        content: input.content,

        source_type: input.source.type,

        source_id: input.source.id,

        source_path: input.source.path ?? null,

        source_version: input.source.version ?? null,

        confidence: input.confidence,

        embedding_status: input.embeddingStatus,

        metadata: input.metadata,
      })
      .select("*")
      .single();

    if (error) {
      throw new Error(`Failed to persist Cyan memory: ${error.message}`);
    }

    return mapRow(data as DbMemoryRow);
  }

  async retrieve(
    scope: MemoryRetrievalScope,
    query: string,
    limit = 20,
  ): Promise<PersistentCyanMemoryRecord[]> {
    const normalized = query.trim();

    if (!normalized) {
      return [];
    }

    const safeLimit = Math.max(1, Math.min(limit, 100));

    let builder = this.supabase
      .from("cyan_memory_records")
      .select("*")
      .eq("organization_id", scope.organizationId);

    if (scope.userId) {
      builder = builder.eq("user_id", scope.userId);
    }

    if (scope.projectId) {
      builder = builder.eq("project_id", scope.projectId);
    }

    if (scope.conversationId) {
      builder = builder.eq("conversation_id", scope.conversationId);
    }

    const { data, error } = await builder
      .ilike("content", `%${escapeLike(normalized)}%`)
      .order("created_at", {
        ascending: false,
      })
      .limit(safeLimit);

    if (error) {
      throw new Error(`Failed to retrieve Cyan memory: ${error.message}`);
    }

    return ((data ?? []) as DbMemoryRow[]).map(mapRow);
  }

  async listRecent(
    scope: MemoryRetrievalScope,
    limit = 20,
  ): Promise<PersistentCyanMemoryRecord[]> {
    const safeLimit = Math.max(1, Math.min(limit, 100));

    let builder = this.supabase
      .from("cyan_memory_records")
      .select("*")
      .eq("organization_id", scope.organizationId);

    if (scope.userId) {
      builder = builder.eq("user_id", scope.userId);
    }

    if (scope.projectId) {
      builder = builder.eq("project_id", scope.projectId);
    }

    if (scope.conversationId) {
      builder = builder.eq("conversation_id", scope.conversationId);
    }

    const { data, error } = await builder
      .order("created_at", {
        ascending: false,
      })
      .limit(safeLimit);

    if (error) {
      throw new Error(`Failed to list recent Cyan memory: ${error.message}`);
    }

    return ((data ?? []) as DbMemoryRow[]).map(mapRow);
  }

  async listBySource(
    organizationId: string,
    source: CyanMemorySourceReference,
  ): Promise<PersistentCyanMemoryRecord[]> {
    const { data, error } = await this.supabase
      .from("cyan_memory_records")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("source_type", source.type)
      .eq("source_id", source.id)
      .order("created_at", {
        ascending: false,
      });

    if (error) {
      throw new Error(`Failed to list Cyan memory by source: ${error.message}`);
    }

    return ((data ?? []) as DbMemoryRow[]).map(mapRow);
  }
}
